\<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import type { ExampleGraphicsBackend } from '../utils/graphicsUrlConfig'
import type { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'

type GraphicsCard = ReturnType<typeof useExampleGraphicsCard>

const props = defineProps<{
  card: GraphicsCard
  lockedReason?: string
  /** 该 route 的插件闭包尚未完成 WebGPU 迁移：仍在卡片上展示真实阻塞项，但不可切换。 */
  blockedReason?: string
}>()

const expanded = ref(true)
const switchDisabled = computed(() => Boolean(props.lockedReason || props.blockedReason))

const phaseLabel = computed(() => {
  switch (props.card.phase.value) {
    case 'creating':
      return '正在初始化'
    case 'ready':
      return '已就绪'
    case 'error':
      return '初始化失败'
    default:
      return '等待创建'
  }
})

const backendOptions: readonly { value: ExampleGraphicsBackend; label: string }[] = [
  { value: 'webgl2', label: 'WebGL 2' },
  { value: 'webgpu', label: 'WebGPU' },
]

function toggle(): void {
  expanded.value = !expanded.value
}

function selectBackend(value: ExampleGraphicsBackend): void {
  if (switchDisabled.value || value === props.card.requestedBackend.value) return
  props.card.selectBackend(value)
  props.card.rebuild(value)
}

/**
 * 静态预检只读插件声明，不创建 renderer/device；请求后端变化时重新计算真实阻塞项。
 * 失败（例如引擎入口不可用）不阻断渲染，交由卡片继续展示未知状态。
 */
function refreshPreflight(): void {
  void props.card.runPreflight().catch(() => undefined)
}

onMounted(refreshPreflight)
watch(() => props.card.requestedBackend.value, refreshPreflight)
</script>

<template>
  <section class="graphics-card">
    <header class="graphics-card-header">
      <div class="graphics-card-title">
        <p class="graphics-card-kicker">Graphics</p>
        <h3>渲染后端</h3>
      </div>
      <div class="graphics-card-header-actions">
        <span class="graphics-card-badge">{{ props.card.actualLabel.value }}</span>
        <button
          type="button"
          class="graphics-card-toggle"
          :aria-expanded="expanded"
          :aria-label="expanded ? '折叠渲染后端设置' : '展开渲染后端设置'"
          @click="toggle"
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" :class="{ 'is-collapsed': !expanded }">
            <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" />
          </svg>
        </button>
      </div>
    </header>

    <div v-show="expanded" class="graphics-card-body">
      <div
        class="graphics-card-segmented"
        role="radiogroup"
        aria-label="选择渲染后端"
        :aria-disabled="switchDisabled"
      >
        <button
          v-for="option in backendOptions"
          :key="option.value"
          type="button"
          role="radio"
          :aria-checked="props.card.requestedBackend.value === option.value"
          :disabled="switchDisabled"
          :data-backend="option.value"
          :class="{ 'is-active': props.card.requestedBackend.value === option.value }"
          @click="selectBackend(option.value)"
        >
          {{ option.label }}
        </button>
      </div>

      <dl class="graphics-card-config">
        <div><dt>preset</dt><dd>{{ props.card.presetLabel.value }}</dd></div>
        <div><dt>请求后端</dt><dd>{{ props.card.requestedLabel.value }}</dd></div>
        <div><dt>实际后端</dt><dd>{{ props.card.actualLabel.value }}</dd></div>
        <div><dt>状态</dt><dd>{{ phaseLabel }}</dd></div>
      </dl>

      <p v-if="props.lockedReason" class="graphics-card-note">
        该页面为固定条件验证页：{{ props.lockedReason }}
      </p>
      <p v-else-if="props.blockedReason" class="graphics-card-note">
        当前无法切换到 WebGPU：{{ props.blockedReason }}
      </p>

      <div v-if="props.card.confirmedBlockers.value.length > 0" class="graphics-card-blockers">
        <p class="graphics-card-blockers-title">当前请求后端存在阻塞</p>
        <ul>
          <li v-for="blocker in props.card.confirmedBlockers.value" :key="blocker.subjectName">
            <strong>{{ blocker.subjectName }}</strong>
            <span>{{ blocker.missingCapabilities.join('、') }}</span>
          </li>
        </ul>
      </div>

      <div v-else-if="props.card.runtimeConfirmations.value.length > 0" class="graphics-card-note">
        以下插件需要在创建后确认设备能力：{{
          props.card.runtimeConfirmations.value.map((entry) => entry.subjectName).join('、')
        }}
      </div>

      <div class="graphics-card-status" aria-live="polite">
        <template v-if="props.card.runtimeError.value">
          <p class="graphics-card-error">{{ props.card.runtimeError.value.message }}</p>
          <button type="button" class="graphics-card-retry" @click="props.card.rebuild()">重试</button>
        </template>
        <template v-else-if="props.card.phase.value === 'ready'">
          当前后端已创建完成。
        </template>
        <template v-else>{{ phaseLabel }}…</template>
      </div>

      <slot name="extensions" />
    </div>
  </section>
</template>

<style scoped>
.graphics-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  background: rgba(15, 23, 36, 0.86);
  border: 1px solid rgba(120, 156, 200, 0.22);
  color: #e6eefc;
}

.graphics-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.graphics-card-title h3 {
  margin: 2px 0 0;
  font-size: 14px;
  font-weight: 600;
}

.graphics-card-kicker {
  margin: 0;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(180, 205, 240, 0.7);
}

.graphics-card-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.graphics-card-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(96, 165, 250, 0.18);
  border: 1px solid rgba(96, 165, 250, 0.35);
}

.graphics-card-toggle {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid rgba(120, 156, 200, 0.28);
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.graphics-card-toggle svg {
  width: 14px;
  height: 14px;
  transition: transform 120ms ease;
}

.graphics-card-toggle svg.is-collapsed {
  transform: rotate(-90deg);
}

.graphics-card-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.graphics-card-segmented {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  padding: 3px;
  border-radius: 8px;
  background: rgba(9, 16, 27, 0.7);
}

.graphics-card-segmented button {
  padding: 6px 0;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.graphics-card-segmented button.is-active {
  background: rgba(96, 165, 250, 0.22);
  border-color: rgba(96, 165, 250, 0.45);
}

.graphics-card-segmented button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.graphics-card-config {
  display: grid;
  gap: 4px;
  margin: 0;
  font-size: 12px;
}

.graphics-card-config > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.graphics-card-config dt {
  color: rgba(180, 205, 240, 0.7);
}

.graphics-card-config dd {
  margin: 0;
}

.graphics-card-note {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(214, 226, 245, 0.85);
}

.graphics-card-blockers ul {
  margin: 4px 0 0;
  padding-left: 16px;
  font-size: 12px;
  display: grid;
  gap: 4px;
}

.graphics-card-blockers-title {
  margin: 0;
  font-size: 12px;
  color: #fca5a5;
}

.graphics-card-status {
  font-size: 12px;
  display: grid;
  gap: 6px;
}

.graphics-card-error {
  margin: 0;
  color: #fca5a5;
}

.graphics-card-retry {
  justify-self: start;
  padding: 4px 10px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid rgba(120, 156, 200, 0.35);
  background: transparent;
  color: inherit;
  cursor: pointer;
}
</style>
